import React, { useEffect, useState } from "react";
import styled from "styled-components";

const StyledWrapper = styled.div`
  width: 30rem;
  height: auto;
  margin: 3rem;
`;

const StyledHeading = styled.h3`
  color: var(--color-grey-700);
  margin-bottom: -1rem;
  align-self: center;
  text-align: center;
`;

const SEGMENT_COUNT = 300;
const ACTIVE_LENGTH = 135; // ~2/5 of 200 segments
const INVISIBLE_LENGTH = 2; // small gaps before and after
const TICK_INTERVAL = 30;
const STROKE_WIDTH = 17;
const TRACK_WIDTH = 615;
const TRACK_HEIGHT = 175;
const RADIUS = TRACK_HEIGHT / 2;

function getRacetrackPoints(segmentCount) {
  const points = [];
  const arcPoints = segmentCount / 4;
  const linePoints = segmentCount / 4;

  // Left arc (bottom to top)
  for (let i = 0; i < arcPoints; i++) {
    const angle = Math.PI / 2 + (Math.PI * i) / arcPoints;
    const x = RADIUS + RADIUS * Math.cos(angle);
    const y = RADIUS + RADIUS * Math.sin(angle);
    points.push({ x, y });
  }

  // Top line (left to right)
  for (let i = 0; i < linePoints; i++) {
    const x = RADIUS + (TRACK_WIDTH - 2 * RADIUS) * (i / linePoints);
    const y = 0;
    points.push({ x, y });
  }

  // Right arc (top to bottom)
  for (let i = 0; i < arcPoints; i++) {
    const angle = (3 * Math.PI) / 2 + (Math.PI * i) / arcPoints;
    const x = TRACK_WIDTH - RADIUS + RADIUS * Math.cos(angle);
    const y = RADIUS + RADIUS * Math.sin(angle);
    points.push({ x, y });
  }

  // Bottom line (right to left)
  for (let i = 0; i < linePoints; i++) {
    const x =
      TRACK_WIDTH - RADIUS - (TRACK_WIDTH - 2 * RADIUS) * (i / linePoints);
    const y = TRACK_HEIGHT;
    points.push({ x, y });
  }

  return points;
}

const WIOSSLoader = ({ text = "Loading. Please Wait" }) => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setOffset((prev) => (prev + 1) % SEGMENT_COUNT);
    }, TICK_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  const points = getRacetrackPoints(SEGMENT_COUNT);

  const segments = points.map((point, i) => {
    const next = points[(i + 1) % SEGMENT_COUNT];

    const inActiveRange =
      (i >= offset && i < offset + ACTIVE_LENGTH + 2 * INVISIBLE_LENGTH) ||
      (offset + ACTIVE_LENGTH + 2 * INVISIBLE_LENGTH > SEGMENT_COUNT &&
        i < (offset + ACTIVE_LENGTH + 2 * INVISIBLE_LENGTH) % SEGMENT_COUNT);

    let color = "var(--color-witron-grey)";

    if (inActiveRange) {
      const relativeIndex = (i - offset + SEGMENT_COUNT) % SEGMENT_COUNT;
      if (
        relativeIndex < INVISIBLE_LENGTH ||
        relativeIndex >= INVISIBLE_LENGTH + ACTIVE_LENGTH
      ) {
        color = "transparent";
      } else {
        color = "var(--color-witron-blue)";
      }
    }

    return (
      <line
        key={i}
        x1={point.x}
        y1={point.y}
        x2={next.x}
        y2={next.y}
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="butt"
      />
    );
  });

  return (
    <StyledWrapper>
      <svg
        viewBox={`-50 0 ${TRACK_WIDTH + 50} ${TRACK_HEIGHT + 60}`}
        preserveAspectRatio="xMidYMid meet"
        shapeRendering="geometricPrecision"
      >
        <g transform="translate(-34, 26)" filter="url(#smooth)">
          {segments}
        </g>

        <defs>
          <filter id="smooth">
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" />
          </filter>
        </defs>
        {/* Static elements */}
        <g transform="scale(SCALE) translate(X, Y)">
          {/* Paste all cls-1 paths here */}
          <path
            d="M24.21,69c7.64,0,15.28-.03,22.92,.07,.6,0,1.59,.87,1.71,1.47,3.17,15.76,6.25,31.53,9.35,47.3,.06,.32,.23,.63,.34,.94,.12-.31,.29-.62,.36-.95,3.32-15.85,6.62-31.7,10-47.53,.11-.51,.94-1.24,1.44-1.24,10-.07,20-.07,30,0,.47,0,1.27,.69,1.37,1.17,3.19,15.95,6.31,31.92,9.44,47.89,.06,.28,.21,.55,.32,.82,.11-.29,.25-.58,.31-.88,3.39-16,6.76-32,10.19-47.98,.09-.43,.84-1.01,1.29-1.01,15.16-.05,30.32-.05,45.48,0,.31,0,.95,.82,.87,1.11-8.74,30.9-17.53,61.78-26.37,92.65-.11,.38-.8,.86-1.21,.86-14.64,.04-29.28,.05-43.92-.01-.44,0-1.14-.63-1.26-1.09-3.85-14.53-7.65-29.08-11.47-43.63-.1-.39-.33-.74-.5-1.11-.14,.36-.32,.7-.41,1.07-3.77,14.52-7.52,29.04-11.34,43.54-.14,.52-.98,1.21-1.49,1.22-14.68,.07-29.36,.06-44.04,0-.51,0-1.33-.64-1.47-1.13C17.37,131.79,8.66,101.05,0,70.29c-.09-.32,.6-1.24,.93-1.24,7.76-.07,15.52-.06,23.28-.06Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M220.11,116.38c0,15.3,.01,30.6-.06,45.9,0,.47-.83,1.36-1.28,1.36-15.08,.07-30.15,.07-45.23,0-.44,0-1.25-.82-1.25-1.25-.06-30.68-.06-61.36,0-92.04,0-.45,.86-1.3,1.32-1.3,15.04-.07,30.07-.07,45.11,0,.46,0,1.32,.85,1.33,1.3,.07,15.34,.06,30.68,.06,46.02Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M281.84,167.19c-18.46-.73-36.73-8.31-48.48-27.4-13.71-22.26-5.6-50.18,18.26-64.66,27.64-16.78,65.22-9.94,83.18,15.13,15.83,22.1,8.89,52.1-15.56,67.31-10.45,6.5-21.87,9.56-37.39,9.62Zm19.11-50.82c-.03-11.24-5.51-16.6-16.95-16.58-11.32,.02-16.51,5.54-16.48,17.53,.03,10.48,5.8,15.78,17.13,15.75,10.93-.03,16.33-5.56,16.3-16.7Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M387.11,166.17c-14.74-.02-28.59-3.71-41.74-10.22-.33-.16-.66-1.05-.5-1.38,3.84-8.34,7.73-16.65,11.67-24.94,.12-.26,1-.46,1.31-.29,8.72,4.86,17.44,9.73,27.93,9.19,1.45-.07,3.12-.25,4.23-1.03,1.22-.86,2.58-2.44,2.63-3.76,.05-1.23-1.3-3.31-2.45-3.66-5.02-1.52-10.22-2.46-15.31-3.77-10.19-2.62-19.52-6.58-23.28-17.56-4.95-14.45,3.37-31.01,19.25-37.74,20.02-8.48,39.75-5.72,59.21,2.39,2.22,.93,2.64,1.62,1.49,3.88-3.53,6.94-6.73,14.04-10.15,21.03-.2,.41-1.44,.8-1.83,.57-7.65-4.41-15.64-7.7-24.68-6.22-1.81,.3-3.32,2.39-4.96,3.66,1.4,1.59,2.53,4.11,4.25,4.61,6.5,1.88,13.28,2.79,19.79,4.65,9.94,2.84,17.61,8.63,19.76,19.28,2.35,11.69-1.58,21.66-10.43,29.48-9.2,8.13-21.13,11.82-36.19,11.84Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M481.74,166.19c-16.22-.05-30.03-3.7-43.14-10.2-.37-.18-.73-1.17-.56-1.54,3.79-8.27,7.65-16.51,11.56-24.73,.14-.29,1.06-.55,1.36-.38,8.4,4.68,16.8,9.37,26.85,9.23,1.34-.02,2.94,.08,3.97-.58,1.59-1.02,3.84-2.6,3.89-4.02,.05-1.46-1.91-3.88-3.46-4.36-5.48-1.68-11.22-2.46-16.74-4.02-10.36-2.94-19.31-7.95-21.43-19.63-2.34-12.91,2.43-23.38,12.99-30.96,11.59-8.31,25.01-10.4,38.65-8.52,9.8,1.36,19.28,4.97,28.88,7.66,.36,.1,.81,1.34,.61,1.76-3.49,7.53-7.06,15.03-10.69,22.49-.19,.38-1.32,.74-1.68,.53-7.68-4.45-15.7-7.77-24.78-6.29-1.81,.3-3.32,2.39-4.97,3.65,1.4,1.59,2.52,4.11,4.24,4.61,6.34,1.84,12.96,2.74,19.33,4.51,10.23,2.84,18.17,8.66,20.26,19.65,2.36,12.41-2.2,22.65-11.92,30.48-10.1,8.14-22.1,10.48-33.21,10.62Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M359.95,.01c42.8,0,85.6,.03,128.4-.01,24.23-.02,45.79,7.73,65.02,22.19,.88,.66,.51,2.98,.72,4.52-.41-.19-.87-.32-1.23-.59-14.28-10.89-30.02-18.55-47.92-21.19-5.12-.76-10.33-1.27-15.5-1.28-87-.08-174-.05-260.99-.14-.55,0-1.35-1.21-1.55-1.98-.09-.36,.99-1.47,1.54-1.47C272.27,0,316.11,.01,359.95,.01Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M361.27,229.5c-44.28,0-88.55,0-132.83-.08-.55,0-1.48-1.11-1.53-1.75-.04-.52,.98-1.61,1.54-1.61,86.11-.07,172.23-.15,258.34-.02,24.26,.04,45.85-7.29,65.11-21.84,.68-.51,1.46-.88,2.19-1.31-.18,1.52,.21,3.8-.63,4.44-13.11,9.89-27.53,17.2-43.82,20.07-7.2,1.27-14.59,1.98-21.9,2.01-42.16,.18-84.31,.09-126.47,.09Z"
            fill="var(--color-witron-grey)"
          />
          <path
            d="M147.12,.04c30.04,0,60.07,0,90.11,.09,.45,0,1.12,1.27,1.24,2.03,.07,.42-.79,1.42-1.23,1.42-57.87,.07-115.74,0-173.61,.1-22.36,.04-42.87,6.45-61.5,18.83-.64,.43-1.41,.66-2.11,.99,.23-1.45-.07-3.68,.76-4.24C15.64,9.23,31.99,2.41,49.88,1.17,68.23-.1,86.7,.26,105.12,.07c14-.14,28-.03,41.99-.03Z"
            fill="var(--color-witron-blue)"
          />
          <path
            d="M147.44,229.56c-28.11,0-56.23,.11-84.34-.03-22.55-.11-43.29-6.5-62.07-18.96-.91-.61-.68-2.94-.99-4.46,.29,.07,.64,.06,.87,.22,19.99,13.64,42.11,19.8,66.29,19.72,56.59-.19,113.18-.07,169.77,.02,.53,0,1.35,1.19,1.47,1.92,.07,.43-.95,1.52-1.49,1.52-29.83,.08-59.67,.07-89.5,.07Z"
            fill="var(--color-witron-blue)"
          />
          ...
        </g>
      </svg>
      <StyledHeading>{text}</StyledHeading>
    </StyledWrapper>
  );
};

export default WIOSSLoader;
